import Fuse from 'fuse.js';
import { CachedSearchedMarketsType, TierType } from 'src/interfaces/idashboard';
import { removeAccent } from 'src/utils/functions';

/*
Successful Example should show following markets in top 3 (as US user)
las => las vegas
san =>  san francisco / san diego
venice => venice in Italy, venice in US
sao => sao paulo
malaysia => kuala lumpur
saudi => jeddah
bangalore => bengaluru
dallas county => dallas (County)
*/

class MarketSearch {
  private markets: CachedSearchedMarketsType[];
  private fuse: Fuse<CachedSearchedMarketsType>;
  private userPrefCountryCode: string;
  private searchInput: string = '';

  constructor({
    markets,
    userPrefCountryCode,
  }: {
    markets: CachedSearchedMarketsType[];
    userPrefCountryCode: string;
  }) {
    this.markets = markets;
    this.userPrefCountryCode = userPrefCountryCode;

    // Create a Fuse instance with our custom sort function.
    this.fuse = new Fuse(this.markets, {
      isCaseSensitive: false,
      ignoreDiacritics: true,
      includeMatches: true,
      useExtendedSearch: true,
      includeScore: true,
      shouldSort: true,
      findAllMatches: true,
      keys: [
        { name: 'searchName', weight: 10 },
        { name: 'city', weight: 1 }, // to increase once we introduce state properly
        { name: 'country', weight: 7 },
      ],
      location: 0, // fuzz option: default is 0
      threshold: 0.3, // fuzz option: default is 0.6
      distance: 100, // fuzz option: default is 100
    });
  }

  getPreferredCountryScore(countryCode: string): number {
    if (this.userPrefCountryCode === countryCode) {
      return 33;
    } else {
      return 18;
    }
  }

  getTierScore(priority: TierType): number {
    switch (priority) {
      case 'T1':
        return 33;
      case 'T2':
        return 30;
      case 'T3':
        return 28;
      case 'S':
        return 20;
      case 'T4':
        return 13;
      case 'T5':
        return 13;
      case 'SR':
        return 13;
      case 'U50':
        return 3;
      case 'Z':
        return 3;
      default:
        return 3;
    }
  }

  getFuzzRankingScore(fuzzRank: number): number {
    return Math.max(33 - fuzzRank * 2, 15);
  }

  computeScore(result: CachedSearchedMarketsType): number {
    let score = 0;

    const preferredCountryScore = this.getPreferredCountryScore(result.countryCode);
    const tierScore = this.getTierScore(result.priority);
    const fuzzRankingScore = this.getFuzzRankingScore(result.fuzzRank);

    score = preferredCountryScore + tierScore + fuzzRankingScore;
    return score;
  }

  calculate(input: string): CachedSearchedMarketsType[] {
    if (!input || input.trim() === '') {
      return [];
    }

    this.searchInput = removeAccent(input).toLowerCase();

    const fuseResults = this.fuse.search(input);
    const results = fuseResults.map((result, index) => ({
      ...result.item,
      matches: result.matches,
      fuzzRank: index + 1,
    }));

    // calculate score for each result
    const resultsWithScore = results.map(result => {
      return {
        ...result,
        score: this.computeScore(result),
      };
    });

    // sort in descending order by score
    resultsWithScore.sort((a, b) => b.score - a.score);

    // Return the top N results (for example, top 7)
    return resultsWithScore.slice(0, 7);
  }
}

export default MarketSearch;
